/**
 * Resolve the effective transcript layout from a saved ``mediaContext`` value
 * and an optional caller fallback.
 *
 * Callers choose the fallback per surface:
 * - **Fullscreen** — ``fallback: 'side'`` (beside the player) unless the user
 *   or upstream ``sourceContext`` saved an explicit position on ``mediaContext``.
 * - **SourceCard inline** (answer-item, Evidence Locker preview) —
 *   ``fallback: 'below'`` so stacked player+transcript stays the default there
 *   without stamping ``below`` onto ``mediaContext`` (which would carry into
 *   fullscreen on maximize).
 * - **Presentation / event modal / timeline** — typically ``fallback: 'side'``.
 *
 * A narrow container may still collapse to a below-stacked layout at render
 * time in ``player.tsx``; that is separate from this resolver.
 *
 * A saved position of ``'auto'`` (legacy — the removed "Auto" controls option)
 * is treated the same as "never set".
 *
 * Lives in its own module (not inline in mediaDisplay.tsx) so both
 * mediaDisplay.tsx and player.tsx can import it without a circular
 * dependency (mediaDisplay.tsx renders MediaPlayer from player.tsx).
 *
 * Exported so it has a direct unit-test surface —
 * see ``mediaDisplay.transcript-position.test.ts`` (TR-1589).
 */
export function resolveTranscriptPosition(opts: {
  mediaContextPosition?: 'side' | 'below' | 'auto' | string;
  fallback?: 'side' | 'below';
}): 'side' | 'below' {
  const saved = opts.mediaContextPosition;
  if (saved === 'side' || saved === 'below') {
    return saved;
  }
  return opts.fallback ?? 'side';
}
